import { FC, MouseEvent, useMemo } from "react";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useAppStore } from "@/store";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds";
import { FloatingNotePreview, ItemPreviewHoveringState } from "@/components/item-preview";
import { dedupeSourceIds } from "./source-utils";

interface SourcesPanelProps {
  noteIds: string[];
  isOpen: boolean;
}

export const SourcesPanel: FC<SourcesPanelProps> = observer(function SourcesPanel({
  noteIds,
  isOpen,
}) {
  const uniqueNoteIds = useMemo(() => dedupeSourceIds(noteIds), [noteIds]);

  if (!isOpen || uniqueNoteIds.length === 0) return null;

  return (
    <Panel>
      {uniqueNoteIds.map(id => (
        <SourceRow key={id} noteId={id} />
      ))}
    </Panel>
  );
});

const SourceRow: FC<{ noteId: string }> = observer(function SourceRow({ noteId }) {
  const { store } = useAppStore();
  const hoverState = useMemo(() => new ItemPreviewHoveringState(), []);
  const noteObservable = store.notes.getNoteObservableById({ noteId });
  const title =
    noteObservable?.suggestionData?.suggested_primary_label ||
    noteObservable?.title ||
    "Source note";
  const excerpt = noteObservable?.secondaryTitle || "";

  const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
    hoverState.setHoveredRect(event.currentTarget.getBoundingClientRect());
    hoverState.handleMouseEnter();
  };

  const handleMouseLeave = () => {
    hoverState.setHoveredRect(null);
    hoverState.handleMouseLeave();
  };

  return (
    <SourceRowWrapper>
      <Row
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          hoverState.deactivateImmediately();
          store.navigation.goToNote({ noteId });
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <RowIcon>
          <MDSIcon
            kind={MDSIconKind.Document}
            size={11}
            boxSize={16}
            color={MDSIconColor.Inherit}
          />
        </RowIcon>
        <RowText>
          <RowTitle>{title}</RowTitle>
          {excerpt && <RowExcerpt>{excerpt}</RowExcerpt>}
        </RowText>
      </Row>
      {noteObservable && <FloatingNotePreview state={hoverState} noteObservable={noteObservable} />}
    </SourceRowWrapper>
  );
});

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["1"]};
  padding: ${({ theme }) => theme.spacing["2"]};
  background: ${({ theme }) => theme.colors.background.button.onChrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`;

const SourceRowWrapper = styled.div`
  min-width: 0;
`;

const Row = styled.button`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr);
  gap: ${({ theme }) => theme.spacing["2"]};
  align-items: start;
  width: 100%;
  padding: 6px 8px;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  border-radius: 6px;
  font-family: inherit;
  color: inherit;
  &:hover {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;

const RowIcon = styled.span`
  color: ${({ theme }) => theme.colors.text.tertiary};
  padding-top: 1px;
`;

const RowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const RowTitle = styled.div`
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RowExcerpt = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
